import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Leaf } from 'lucide-react';
import CannabisLeaf from '../components/CannabisLeaf';

function formatError(detail) {
  if (!detail) return 'Ein Fehler ist aufgetreten.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

export default function LoginPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        if (!name.trim()) { setError('Name ist erforderlich'); setLoading(false); return; }
        await register(email, password, name);
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden"
      style={{ background: '#050A07' }}>
      {/* Background */}
      <div className="absolute inset-0 opacity-5"
        style={{ backgroundImage: 'url(https://images.pexels.com/photos/3117220/pexels-photo-3117220.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(16,185,129,0.08) 0%, transparent 70%)' }} />

      <div className="relative w-full max-w-sm animate-slide-up">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}>
            <CannabisLeaf size={32} color="white" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-emerald-50">GrowManager</h1>
          <p className="text-emerald-100/50 text-sm mt-1">Cannabis Grow Projekte verwalten</p>
        </div>

        {/* Card */}
        <div className="card-grow p-6" style={{ background: '#0A120D' }}>
          {/* Tabs */}
          <div className="flex rounded-lg p-1 mb-6" style={{ background: '#050A07', border: '1px solid #1B2A21' }}>
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${mode === 'login' ? 'bg-emerald-500 text-white' : 'text-emerald-100/50 hover:text-emerald-100'}`}
              data-testid="login-tab">
              Anmelden
            </button>
            <button
              onClick={() => setMode('register')}
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${mode === 'register' ? 'bg-emerald-500 text-white' : 'text-emerald-100/50 hover:text-emerald-100'}`}
              data-testid="register-tab">
              Registrieren
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1.5 uppercase tracking-wider">Name</label>
                <input
                  type="text"
                  className="input-grow"
                  placeholder="Dein Name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  data-testid="register-name-input"
                />
              </div>
            )}
            <div>
              <label className="block text-xs text-emerald-100/60 mb-1.5 uppercase tracking-wider">E-Mail</label>
              <input
                type="email"
                className="input-grow"
                placeholder="deine@email.de"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                data-testid="email-input"
              />
            </div>
            <div>
              <label className="block text-xs text-emerald-100/60 mb-1.5 uppercase tracking-wider">Passwort</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  className="input-grow pr-10"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  data-testid="password-input"
                />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-100/40 hover:text-emerald-100 transition-colors">
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-red-400 text-sm p-3 rounded-lg" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}
                data-testid="auth-error">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} className="btn-primary w-full"
              data-testid={mode === 'login' ? 'login-submit-btn' : 'register-submit-btn'}>
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  <span>Bitte warten...</span>
                </div>
              ) : mode === 'login' ? 'Anmelden' : 'Konto erstellen'}
            </button>
          </form>

          {mode === 'login' && (
            <p className="text-center text-xs text-emerald-100/30 mt-4">
              Demo: admin@growmanager.de / admin123
            </p>
          )}
        </div>

        <div className="flex items-center justify-center gap-2 mt-6 text-emerald-100/20 text-xs">
          <Leaf size={12} />
          <span>GrowManager v1.0</span>
        </div>
      </div>
    </div>
  );
}
